/**
 * Teslyn Cloud's sign-off calls (MB's R120; docs/specs/cloud/docuseal.md; 20261027130000_jsp_signoff.sql): the
 * organisation's sign-off requests with their projects (`my_signoff_requests`, the staff policies decide), and the
 * `docuseal` function's 5-minute file links, "Check DocuSeal now" and "Withdraw". What a person may do is decided by
 * the database and the function.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SignoffRequestRow } from '@teslyn/domain';

export interface SignoffListRow extends SignoffRequestRow {
  readonly project_number: string;
  readonly project_title: string;
  readonly project_status: string;
}

export async function loadSignoffs(sb: SupabaseClient, tenantId: string): Promise<SignoffListRow[]> {
  const { data, error } = await sb.rpc('my_signoff_requests', { p_tenant: tenantId });
  if (error) throw new Error(error.message);
  return (Array.isArray(data) ? data : []) as SignoffListRow[];
}

async function words(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: Response } | null)?.context;
  const body = context && typeof context.json === 'function' ? await context.json().catch(() => null) : null;
  const w = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
  return typeof w === 'string' && w ? w : fallback;
}

/** A 5-minute link to the unsigned or signed PDF or the audit log. */
export async function signoffFileUrl(
  sb: SupabaseClient,
  requestId: string,
  which: 'unsigned' | 'signed' | 'audit',
): Promise<string> {
  const { data, error } = await sb.functions.invoke<{ url?: string }>(
    `docuseal/file?request=${encodeURIComponent(requestId)}&which=${which}`,
    { method: 'GET' },
  );
  if (error || !data?.url)
    throw new Error(await words(error, 'The file could not be opened. Try again shortly.'));
  return data.url;
}

/** "Check DocuSeal now" and "Withdraw". */
export async function signoffAction(
  sb: SupabaseClient,
  action: 'refresh' | 'withdraw',
  requestId: string,
): Promise<void> {
  const { error } = await sb.functions.invoke(`docuseal/${action}`, { body: { request: requestId } });
  if (error) throw new Error(await words(error, 'Teslyn Cloud could not reach DocuSeal. Try again shortly.'));
}
